import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

import BlueButton from 'components/Shared/BlueButton/BlueButton';
import RequestAccessDialog from './RequestAccessDialog';
import LearnMoreDialog, {
  LEARN_MORE_ROWS,
  chip,
  timedChip,
} from './LearnMoreDialog';
import { HeroArt, CrewArt } from './LandingArt';
import VideoDialog, { POSTER_URL } from './VideoDialog';
import {
  requestInspectAccess,
  markInspectLandingSeen,
  readInspectAccessRequested,
} from 'store/actions/inspectLanding';
// The result chips on this page are the Inspect module's own classes; the module may never have been opened.
import 'components/Inspect/style.css';
import './style.css';

// Spent on the page this long counts as seen: the sidebar badge becomes a dot (once per user per session).
var SEEN_DELAY_MS = 3000;
// The pause state of the two animation controls, kept for the session (cleared with the other Inspect keys).
var ANIMATIONS_KEY = 'inspectLandingAnimations';
var GAUGE_TRACK = '226.2 75.4';
var GAUGE_ARC = '185.5 116.1';

// Example chips of the five list rows.
var ROW_EXAMPLES = {
  due: function (t) {
    return [
      timedChip('due-soon', t('inspectLanding:chipDueSoon'), t('inspectLanding:exDueSoonTime')),
      timedChip('overdue', t('inspectLanding:chipOverdue'), t('inspectLanding:exOverdueTime')),
      chip('scheduled', t('inspectLanding:chipScheduled')),
    ];
  },
  results: function (t) {
    return [
      chip('pass', t('inspectLanding:chipPass')),
      chip('fail', t('inspectLanding:chipFail')),
      chip('incomplete', t('inspectLanding:chipIncomplete')),
    ];
  },
  repairs: function (t) {
    return [
      chip('fail', t('inspectLanding:chipFail')),
      <span key="arrow" className="material-symbols-outlined" aria-hidden="true">arrow_forward</span>,
      chip('corrected', t('inspectLanding:chipCorrected')),
    ];
  },
  skipped: function (t) {
    return [
      timedChip('neglected', t('inspectLanding:chipNeglected'), t('inspectLanding:exNeglectedTime')),
    ];
  },
};

function accountUUIDOf(props) {
  return (props.currentAccount && props.currentAccount.uuid) || '';
}

function readPaused() {
  try {
    return sessionStorage.getItem(ANIMATIONS_KEY) === 'paused';
  } catch (e) {
    return false;
  }
}

function writePaused(paused) {
  try {
    if (paused) {
      sessionStorage.setItem(ANIMATIONS_KEY, 'paused');
    } else {
      sessionStorage.removeItem(ANIMATIONS_KEY);
    }
  } catch (e) {
    // storage unavailable: the choice just does not persist
  }
}

function hasObserver() {
  return typeof window !== 'undefined' && !!window.IntersectionObserver;
}

function prefersReducedMotion() {
  try {
    return !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {
    return false;
  }
}

// Elements with this class stay hidden until they scroll into view, only while the root has
// inspect-landing--reveal: no observer or reduced motion means no hidden state at all.
var RV = ' inspect-landing-rv';
var RV_IN = 'inspect-landing-rv-in';
// The page is designed at REF_WIDTH and scales as one piece to its content width, within these limits, so type,
// spacing and the drawn figures keep their proportions from tablets to large monitors. Below the limit the layout
// flows: the hero stacks, the list tightens, and on a phone everything is one column. Above it the column stops
// growing (at most 1416px on screen, REF_WIDTH x MAX_SCALE) and centres: a full-width page is hard to read.
var REF_WIDTH = 1180;
var MIN_SCALE = 0.9;
var MAX_SCALE = 1.2;

// width: the page's content width in CSS pixels; 0 when it cannot be measured.
export function landingFit(width) {
  if (!width) return { scale: 1, stack: false, compact: false, phone: false };
  var scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, width / REF_WIDTH));
  scale = Math.round(scale * 100) / 100;
  var inner = width / scale;
  var phone = inner < 700;
  // A phone-width page keeps full-size text and targets: its layout flows instead (ui-standards: never shrink to fit).
  return { scale: phone ? 1 : scale, stack: inner < 1050, compact: inner < 1000, phone: phone };
}

function supportsZoom() {
  return !!window.CSS && !!window.CSS.supports && window.CSS.supports('zoom', '1');
}

// An element starts once it is 5% of the page height above the bottom edge (Jeremy 2026-11-04).
var REVEAL_MARGIN = '0px 0px -5% 0px';
// List rows revealed in one batch start 80ms apart, up to 300ms.
var ROW_STAGGER_MS = 80;
var ROW_STAGGER_MAX_MS = 300;

// What Inspect users see while Inspect is off for the stack and the user.
export class InspectLandingPage extends Component {
  constructor(props) {
    super(props);
    var reveal = hasObserver() && !prefersReducedMotion();
    this.state = {
      dialogOpen: false,
      openRow: null,
      videoOpen: false,
      paused: readPaused(),
      // each loop waits for its element's reveal; the video call-out and the crew figure also wait for the
      // first scroll of the page (scrolled)
      waiting: { hero: reveal, video: reveal, crew: reveal },
      scrolled: !reveal,
      reveal: reveal,
      fit: landingFit(0),
      requestState: readInspectAccessRequested(accountUUIDOf(props)),
    };
    this._isMounted = false;
    this._seenTimer = null;
    this._revealObserver = null;
    this._revealEndObserver = null;
    this._root = null;
    this._resizeObserver = null;
    this._wraps = { hero: null, video: null, crew: null };
    this._scrollEl = null;
    this.openDialog = this.openDialog.bind(this);
    this.openVideo = this.openVideo.bind(this);
    this.closeVideo = this.closeVideo.bind(this);
    this.closeDialog = this.closeDialog.bind(this);
    this.handleSent = this.handleSent.bind(this);
    this.closeLearnMore = this.closeLearnMore.bind(this);
    this.requestFromLearnMore = this.requestFromLearnMore.bind(this);
    this.togglePause = this.togglePause.bind(this);
    this.handleReveal = this.handleReveal.bind(this);
    this.handleRevealEnd = this.handleRevealEnd.bind(this);
    this.setRoot = this.setRoot.bind(this);
    this.measure = this.measure.bind(this);
    this.setHeroWrap = this.setHeroWrap.bind(this);
    this.setVideoWrap = this.setVideoWrap.bind(this);
    this.setCrewWrap = this.setCrewWrap.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.startSeenTimer();
    var revealing = this.startReveal();
    var fitting = this.startFit();
    if (revealing) this.startScrollWatch(fitting);
  }

  componentDidUpdate(prevProps, prevState) {
    var uuid = accountUUIDOf(this.props);
    if (uuid !== accountUUIDOf(prevProps)) {
      // The gate stays mounted across a stack switch on the same route.
      this.clearSeenTimer();
      this.startSeenTimer();
      this.setState({
        requestState: readInspectAccessRequested(uuid),
        dialogOpen: false,
        openRow: null,
        videoOpen: false,
      });
    }
    if (prevState.fit !== this.state.fit) this.checkScrolled();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.clearSeenTimer();
    this.stopScrollWatch();
    this.stopReveal();
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    } else {
      window.removeEventListener('resize', this.measure);
    }
  }

  // Runs before the first paint, so the page never shows at the wrong scale.
  // Returns true when the first measure() queued a fit change.
  startFit() {
    var queued = this.measure();
    if (window.ResizeObserver && this._root) {
      this._resizeObserver = new window.ResizeObserver(this.measure);
      this._resizeObserver.observe(this._root);
    } else {
      window.addEventListener('resize', this.measure);
    }
    return queued;
  }

  measure() {
    var root = this._root;
    if (!root || !this._isMounted) return false;
    this.checkScrolled();
    // Without zoom the page keeps its designed size and only the layout flows.
    var style = window.getComputedStyle(root);
    var padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
    var fit = landingFit(root.clientWidth - padding);
    if (!supportsZoom()) fit.scale = 1;
    var now = this.state.fit;
    if (fit.scale === now.scale && fit.stack === now.stack && fit.compact === now.compact && fit.phone === now.phone) return false;
    this.setState({ fit: fit });
    return true;
  }

  startSeenTimer() {
    var user = this.props.currentUser;
    if (user && user.dashboardSettings && user.dashboardSettings.inspectLandingSeen === true) return;
    var self = this;
    this._seenTimer = setTimeout(function () {
      self._seenTimer = null;
      if (self.props.markInspectLandingSeen) self.props.markInspectLandingSeen();
    }, SEEN_DELAY_MS);
  }

  clearSeenTimer() {
    if (this._seenTimer) {
      clearTimeout(this._seenTimer);
      this._seenTimer = null;
    }
  }

  // The video call-out and the crew figure start after the page has scrolled once: the first scroll event on
  // the page root, or a root that has nothing to scroll (large monitor) or is already scrolled.
  // skipCheck: the mount fit is still queued, so the componentDidUpdate that applies it makes the first check.
  startScrollWatch(skipCheck) {
    var el = this._root;
    if (!el) return;
    this._scrollEl = el;
    el.addEventListener('scroll', this.handleScroll);
    if (!skipCheck) this.checkScrolled();
  }

  stopScrollWatch() {
    if (this._scrollEl) this._scrollEl.removeEventListener('scroll', this.handleScroll);
    this._scrollEl = null;
  }

  checkScrolled() {
    var el = this._scrollEl;
    if (this.state.scrolled || !el) return;
    var fits = 0 < el.clientHeight && el.scrollHeight <= el.clientHeight;
    if (fits || 0 < el.scrollTop) this.handleScroll();
  }

  handleScroll() {
    this.stopScrollWatch();
    if (!this._isMounted || this.state.scrolled) return;
    this.setState({ scrolled: true });
  }

  // Scroll reveal. The scrolling element is the page root, not the window. An element starts once it is
  // REVEAL_MARGIN into the page, so the viewer sees it happen; a figure's loop starts with its reveal (the
  // video call-out and the crew figure also wait for the first scroll). The closing row can never get that far
  // up, so when it enters, everything left is shown. Anything that goes wrong shows everything.
  // Returns true when the observers run.
  startReveal() {
    if (!this.state.reveal) return false;
    try {
      var root = this._root;
      var observer = new window.IntersectionObserver(this.handleReveal, { root: root, rootMargin: REVEAL_MARGIN, threshold: 0 });
      this._revealObserver = observer;
      Array.prototype.forEach.call(root.querySelectorAll('.inspect-landing-rv'), function (el) {
        observer.observe(el);
      });
      this._revealEndObserver = new window.IntersectionObserver(this.handleRevealEnd, { root: root, threshold: 0 });
      this._revealEndObserver.observe(root.querySelector('.inspect-landing-close-in'));
      return true;
    } catch (e) {
      this.stopReveal();
      this.setState({ reveal: false, waiting: { hero: false, video: false, crew: false }, scrolled: true });
      return false;
    }
  }

  stopReveal() {
    if (this._revealObserver) this._revealObserver.disconnect();
    if (this._revealEndObserver) this._revealEndObserver.disconnect();
    this._revealObserver = null;
    this._revealEndObserver = null;
  }

  handleRevealEnd(entries) {
    if (!this._root) return;
    var reached = entries.some(function (entry) {
      return entry.isIntersecting;
    });
    if (!reached) return;
    Array.prototype.forEach.call(this._root.querySelectorAll('.inspect-landing-rv'), function (el) {
      el.classList.add(RV_IN);
    });
    this.stopReveal();
    this.setState({ waiting: { hero: false, video: false, crew: false } });
  }

  handleReveal(entries) {
    var observer = this._revealObserver;
    if (!observer) return;
    var wraps = this._wraps;
    var started = [];
    this.staggerRows(entries);
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      entry.target.classList.add(RV_IN);
      observer.unobserve(entry.target);
      ['hero', 'video', 'crew'].forEach(function (key) {
        if (wraps[key] && wraps[key] === entry.target) started.push(key);
      });
    });
    if (started.length === 0) return;
    this.setState(function (state) {
      var waiting = Object.assign({}, state.waiting);
      started.forEach(function (key) {
        waiting[key] = false;
      });
      return { waiting: waiting };
    });
  }

  // List rows that enter in one batch start in page order, ROW_STAGGER_MS apart. A lone row starts at once.
  // A failed stagger means no delay, never a skipped reveal.
  staggerRows(entries) {
    try {
      var batch = entries
        .filter(function (entry) {
          return entry.isIntersecting && entry.target.classList.contains('inspect-landing-list-row');
        })
        .map(function (entry) {
          return entry.target;
        });
      if (batch.length < 2 || !this._root) return;
      var order = Array.prototype.slice.call(this._root.querySelectorAll('tr.inspect-landing-list-row'));
      batch.sort(function (a, b) {
        return order.indexOf(a) - order.indexOf(b);
      });
      batch.forEach(function (el, i) {
        el.style.transitionDelay = Math.min(i * ROW_STAGGER_MS, ROW_STAGGER_MAX_MS) + 'ms';
      });
    } catch (e) {
      // no delay
    }
  }

  setRoot(el) {
    this._root = el;
  }

  setHeroWrap(el) {
    this._wraps.hero = el;
  }

  setVideoWrap(el) {
    this._wraps.video = el;
  }

  setCrewWrap(el) {
    this._wraps.crew = el;
  }

  togglePause() {
    var paused = !this.state.paused;
    writePaused(paused);
    this.setState({ paused: paused });
  }

  openDialog() {
    this.setState({ dialogOpen: true });
  }

  closeDialog() {
    this.setState({ dialogOpen: false });
  }

  openVideo() {
    this.setState({ videoOpen: true });
  }

  closeVideo() {
    this.setState({ videoOpen: false });
  }

  openLearnMore(row) {
    this.setState({ openRow: row });
  }

  closeLearnMore() {
    this.setState({ openRow: null });
  }

  // Request Access inside a Learn More dialog: close it and open the confirm dialog.
  requestFromLearnMore() {
    this.setState({ openRow: null, dialogOpen: true });
  }

  handleSent(status, accountUUID) {
    if (!this._isMounted) return;
    if (accountUUID !== accountUUIDOf(this.props)) return;
    this.setState({ requestState: status });
  }

  renderPause() {
    var t = this.props.t;
    var paused = this.state.paused;
    return (
      <button
        type="button"
        className="inspect-landing-pause"
        aria-pressed={paused ? 'true' : 'false'}
        aria-label={t(paused ? 'inspectLanding:playAnimations' : 'inspectLanding:pauseAnimations')}
        onClick={this.togglePause}
      >
        <span className="material-symbols-outlined" aria-hidden="true">{paused ? 'play_arrow' : 'pause'}</span>
      </button>
    );
  }

  renderGauge(t) {
    return (
      <span className="inspect-landing-gauge">
        <span className="inspect-landing-gauge-d">
          <svg width="56" height="56" viewBox="0 0 120 120" aria-hidden="true">
            <circle
              className="inspect-landing-gauge-track"
              cx="60"
              cy="60"
              r="48"
              fill="none"
              strokeWidth="10"
              strokeDasharray={GAUGE_TRACK}
              transform="rotate(135 60 60)"
            />
            <circle
              className="inspect-landing-gauge-arc"
              cx="60"
              cy="60"
              r="48"
              fill="none"
              strokeWidth="10"
              strokeLinecap="round"
              strokeDasharray={GAUGE_ARC}
              transform="rotate(135 60 60)"
            />
          </svg>
          <span className="inspect-landing-gauge-n" aria-hidden="true">
            <span className="inspect-landing-gauge-score">{t('inspectLanding:gaugeScore')}</span>
            <span className="inspect-landing-gauge-band">{t('inspectLanding:gaugeBand')}</span>
          </span>
        </span>
        <span className="inspect-landing-gauge-tag">
          <span className="sr-only">{t('inspectLanding:gaugeSr')}{' '}</span>
          {t('inspectLanding:gaugeTag')}
        </span>
      </span>
    );
  }

  renderRows(t) {
    var self = this;
    return LEARN_MORE_ROWS.map(function (row) {
      var name = t('inspectLanding:' + row + 'Name');
      return (
        <tr key={row} className={'inspect-landing-list-row' + RV}>
          <th scope="row">{name}</th>
          <td className="inspect-landing-list-what">{t('inspectLanding:' + row + 'What')}</td>
          <td className="inspect-landing-list-ex">
            {row === 'picture' ? (
              self.renderGauge(t)
            ) : (
              <span className="inspect-landing-chip-row">{ROW_EXAMPLES[row](t)}</span>
            )}
          </td>
          <td className="inspect-landing-list-more">
            <button
              type="button"
              className="inspect-landing-link-btn"
              aria-haspopup="dialog"
              aria-label={t('inspectLanding:learnMoreAbout', { feature: name.toLowerCase() })}
              onClick={function () {
                self.openLearnMore(row);
              }}
            >
              {t('inspectLanding:learnMore')}
            </button>
          </td>
        </tr>
      );
    });
  }

  render() {
    var props = this.props;
    var t = props.t;
    var user = props.currentUser || {};
    var stackName = (props.currentAccount && props.currentAccount.name) || '';
    var contactEmail = (user.contactInfo && user.contactInfo.email) || '';
    var contactPhone = (user.contactInfo && user.contactInfo.phone) || '';
    var state = this.state;
    var requestState = state.requestState;
    var sent = !!requestState;
    var note = t('inspectLanding:notOnForStack', { stack: stackName });
    var closeLine = t('inspectLanding:closeLine');
    if (requestState === 'sent') {
      note = t('inspectLanding:sentCaption');
      closeLine = note;
    }
    if (requestState === 'alreadyRequested') {
      note = t('inspectLanding:alreadyCaption');
      closeLine = note;
    }
    var rootClass =
      'inner-page inspect-landing' +
      (state.reveal ? ' inspect-landing--reveal' : '') +
      (state.fit.stack ? ' inspect-landing--stack' : '') +
      (state.fit.compact ? ' inspect-landing--compact' : '') +
      (state.fit.phone ? ' inspect-landing--phone' : '') +
      (state.paused ? ' inspect-landing--paused' : '') +
      (state.openRow !== null || state.dialogOpen || state.videoOpen ? ' inspect-landing--held' : '');
    var requestButton = (
      <BlueButton
        variant="primary"
        label={t(sent ? 'inspectLanding:requestSent' : 'inspectLanding:requestAccess')}
        disabled={sent}
        onClick={this.openDialog}
      />
    );
    var self = this;
    var videoWaits = state.waiting.video || !state.scrolled;
    var crewWaits = state.waiting.crew || !state.scrolled;

    return (
      <div className={rootClass} ref={this.setRoot}>
        <div className="inspect-landing-canvas" style={state.fit.scale !== 1 ? { zoom: state.fit.scale } : undefined}>
        <section className="inspect-landing-hero">
          <div className="inspect-landing-hero-top">
            <div className={'inspect-landing-hero-copy' + RV}>
              <div className="inspect-landing-chips">
                <span className="inspect-landing-chip-new">{t('inspectLanding:chipNew')}</span>
                <span className="inspect-landing-chip-locked">
                  <span className="material-symbols-outlined" aria-hidden="true">lock</span>
                  {t('inspectLanding:chipLocked')}
                </span>
              </div>
              <h2 className="inspect-landing-hero-title">{t('inspectLanding:heroTitle')}</h2>
              <p className="inspect-landing-hero-lede">{t('inspectLanding:heroLede')}</p>
              {requestButton}
              <p className="inspect-landing-note">{note}</p>
            </div>
            <div className={'inspect-landing-figure-wrap' + RV} ref={this.setHeroWrap}>
              {this.renderPause()}
              <HeroArt t={t} waiting={state.waiting.hero} />
            </div>
          </div>
          <div className={'inspect-landing-video' + RV} ref={this.setVideoWrap}>
            <button
              type="button"
              className="inspect-landing-video-frame"
              aria-haspopup="dialog"
              aria-label={t('inspectLanding:videoPlay')}
              onClick={this.openVideo}
            >
              <img className="inspect-landing-video-poster" src={POSTER_URL} alt="" width="1280" height="720" />
              <span className="inspect-landing-play" aria-hidden="true">
                <span className="material-symbols-outlined">play_arrow</span>
              </span>
              <svg className={'inspect-landing-video-beam' + (videoWaits ? ' inspect-landing-wait' : '')} viewBox="0 0 280 158" preserveAspectRatio="none" aria-hidden="true">
                <rect x="1.5" y="1.5" width="277" height="155" rx="7" pathLength="100" />
              </svg>
            </button>
            <div>
              <h3 className="inspect-landing-video-h">{t('inspectLanding:videoTitle')}</h3>
              <p className="inspect-landing-video-p">{t('inspectLanding:videoBody')}</p>
            </div>
          </div>
        </section>

        <section className="inspect-landing-adds">
          <h2 className={'inspect-landing-section-title' + RV}>{t('inspectLanding:listTitle')}</h2>
          <table className="inspect-landing-list">
            <thead className="sr-only">
              <tr>
                <th scope="col">{t('inspectLanding:colFeature')}</th>
                <th scope="col">{t('inspectLanding:colWhat')}</th>
                <th scope="col">{t('inspectLanding:colExample')}</th>
                <th scope="col">{t('inspectLanding:colMore')}</th>
              </tr>
            </thead>
            <tbody>{this.renderRows(t)}</tbody>
          </table>
        </section>

        <section className="inspect-landing-crew">
          <div className="inspect-landing-crew-in">
            <div className={'inspect-landing-crew-text' + RV}>
              <div className="inspect-landing-crew-head">
                <h2 className="inspect-landing-section-title">{t('inspectLanding:crewTitle')}</h2>
                {this.renderPause()}
              </div>
              <p className="inspect-landing-crew-p">{t('inspectLanding:crewLine')}</p>
              <ol className={'inspect-landing-steps' + (crewWaits ? ' inspect-landing-wait' : '')}>
                {[1, 2, 3].map(function (n) {
                  return (
                    <li key={n}>
                      <span className="inspect-landing-step-n" aria-hidden="true">
                        {n}
                        <span className={'inspect-landing-step-on inspect-landing-step-lit-' + n}>{n}</span>
                      </span>
                      <span className="inspect-landing-step-t">{t('inspectLanding:crewStep' + n)}</span>
                      <span className="inspect-landing-step-d">{t('inspectLanding:crewStep' + n + 'Detail')}</span>
                    </li>
                  );
                })}
              </ol>
              <p className="inspect-landing-crew-done">
                <span className="inspect-landing-step-n inspect-landing-step-done material-symbols-outlined" aria-hidden="true">check</span>
                <span className="inspect-landing-step-t">{t('inspectLanding:crewDone')}</span>
              </p>
            </div>
            <div className={'inspect-landing-figure-wrap' + RV} ref={this.setCrewWrap}>
              <CrewArt t={t} waiting={crewWaits} />
            </div>
          </div>
        </section>

        <div className="inspect-landing-close">
          <div className={'inspect-landing-close-in' + RV}>
            <div>
              <h2 className="inspect-landing-close-h">{t('inspectLanding:closeTitle', { stack: stackName })}</h2>
              <p className="inspect-landing-close-line">{closeLine}</p>
            </div>
            {requestButton}
          </div>
        </div>
        </div>

        {LEARN_MORE_ROWS.map(function (row) {
          return (
            <LearnMoreDialog
              key={row}
              row={row}
              open={state.openRow === row}
              onClose={self.closeLearnMore}
              onRequestAccess={self.requestFromLearnMore}
              requestSent={sent}
              t={t}
            />
          );
        })}
        <RequestAccessDialog
          open={state.dialogOpen}
          onClose={this.closeDialog}
          onSend={props.requestInspectAccess}
          onSent={this.handleSent}
          accountUUID={accountUUIDOf(props)}
          contactEmail={contactEmail}
          contactPhone={contactPhone}
          stackName={stackName}
          t={t}
        />
        <VideoDialog open={state.videoOpen} onClose={this.closeVideo} t={t} />
      </div>
    );
  }
}

InspectLandingPage.propTypes = {
  t: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
  currentAccount: PropTypes.object,
  requestInspectAccess: PropTypes.func.isRequired,
  markInspectLandingSeen: PropTypes.func,
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser,
    currentAccount: state.accounts && state.accounts.currentAccount,
  };
}

export default withTranslation()(
  connect(mapStateToProps, { requestInspectAccess, markInspectLandingSeen })(
    InspectLandingPage,
  ),
);
